/**
 * Experts API服务
 */
import apiClient from './client'

export interface Expert {
  id: string
  name: string
  title: string
  department: string
  avatar: string
  rating: number
  status: 'online' | 'busy' | 'offline'
  specialties: string[]
  experience: number
  education: string
  consultationFee: number
  nextAvailable: string
  patientCount: number
  satisfactionRate: number
  bio?: string
  achievements?: string[]
  workingHours?: {
    monday: string[]
    tuesday: string[]
    wednesday: string[]
    thursday: string[]
    friday: string[]
    saturday: string[]
    sunday: string[]
  }
}

export interface ExpertAvailability {
  time: string
  available: boolean
}

export interface ExpertReview {
  id: string
  patientName: string
  rating: number
  comment: string
  date: string
  disease: string
}

export interface ExpertReviewsResponse {
  reviews: ExpertReview[]
  total: number
  averageRating: number
  ratingDistribution: {
    [key: string]: number
  }
}

/**
 * 获取推荐专家列表
 */
export const getFeaturedExperts = async (): Promise<Expert[]> => {
  const response = await apiClient.get('/v1/experts/featured')
  return response as unknown as Expert[]
}

/**
 * 获取专家详细信息
 */
export const getExpertDetails = async (expertId: string): Promise<Expert> => {
  const response = await apiClient.get(`/v1/experts/${expertId}`)
  return response as unknown as Expert
}

/**
 * 搜索专家
 */
export const searchExperts = async (params: {
  keyword?: string
  department?: string
  specialty?: string
  status?: string
  page?: number
  limit?: number
}): Promise<{
  experts: Expert[]
  total: number
  page: number
  limit: number
  totalPages: number
}> => {
  const response = await apiClient.get('/v1/experts/search', { params })
  return response as unknown as {
    experts: Expert[]
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

/**
 * 获取专家可预约时间
 */
export const getExpertAvailability = async (
  expertId: string,
  date?: string
): Promise<ExpertAvailability[]> => {
  const response = await apiClient.get(`/v1/experts/${expertId}/availability`, {
    params: { date }
  })
  return (response as unknown as { data: ExpertAvailability[] }).data || response as unknown as ExpertAvailability[]
}

/**
 * 获取专家评价
 */
export const getExpertReviews = async (
  expertId: string,
  page: number = 1,
  limit: number = 10
): Promise<ExpertReviewsResponse> => {
  const response = await apiClient.get(`/v1/experts/${expertId}/reviews`, {
    params: { page, limit }
  })
  return response as unknown as ExpertReviewsResponse
}

/**
 * 获取科室专家列表
 */
export const getExpertsByDepartment = async (
  departmentId: string,
  page: number = 1,
  limit: number = 20
): Promise<{
  experts: Expert[]
  total: number
  department: string
  page: number
  limit: number
}> => {
  const response = await apiClient.get(`/v1/experts/departments/${departmentId}`, {
    params: { page, limit }
  })
  return response as unknown as {
    experts: Expert[]
    total: number
    department: string
    page: number
    limit: number
  }
}

